import { decode } from "../../utils/crypto/crypto";
// import jwt_decode from 'jsonwebtoken';
import {
  signInWithEmailAndPassword,
  signOut,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { Auth } from "../../utils/firebase/firebase.config";
import { AUTH_CONFIG } from "./token.config";
import { get, put } from "../../utils/axios/axios.config";
import { showUnauthorizedUserMessage } from "../../store/actions";
import store from "../../store";
import { getRelativeRoleName } from "../../config/Roles.config";

// Get auth user details by decoding user token
export const getAuthUser = () => {
  const authUser = localStorage.getItem(process.env.REACT_APP_DEV_AUTH_USER);
  if (authUser && authUser !== "undefined") {
    const userData = decode(authUser);
    return JSON.parse(userData);
  }
  return {};
};

export const getAuthClaims = () => {
  return new Promise((resolve, reject) => {
    onAuthStateChanged(Auth, async (user) => {
      if (user) {
        try {
          Auth.currentUser.getIdToken(true).then((token) => {
            localStorage.setItem(
              process.env.REACT_APP_DEV_USER_TOKEN,
              JSON.stringify({
                accessToken: token,
              })
            );
          });
          const token = await user.getIdTokenResult();
          resolve(token.claims);
        } catch (error) {
          reject(error);
        }
      } else {
        resolve(null);
      }
    });
  });
};

// Function to update password in firebase auth
export const updateUserPassword = async (newPassword) => {
  const user = Auth.currentUser;
  if (user) {
    try {
      await updatePassword(user, newPassword);
    } catch (error) {
      console.log("Error in updateUserPassword: ", error);
    }
  } else {
    console.log("No user found.!");
  }
};

export const getUserRoleAndScreens = () => {
  const authUser = JSON.parse(
    localStorage.getItem(process.env.REACT_APP_DEV_AUTH_USER)
  );
  if (authUser && authUser !== "undefined") {
    return {
      role: authUser && authUser.role?.toLowerCase(),
      // assignedBranches: authUser?.assignedBranches || [],
      // assignedDepartments: authUser?.assignedBranches?.[0]?.assignedDepartments ?? [],
      assignedBranches: authUser?.assignedBranches
        ? [...authUser.assignedBranches].sort((a, b) => a.name.localeCompare(b.name))
        : [],
      assignedDepartments: authUser?.assignedBranches?.[0]?.assignedDepartments
        ? [...authUser.assignedBranches[0].assignedDepartments].sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        : [],
      departmentIds: authUser?.departmentIds || [],
      appRoleName: getRelativeRoleName(authUser?.appRoleName?.toLowerCase()),
      accountId: authUser?.accountId || null,
      userUid: authUser?.uid,
    };
  }
  return {};
};

// firebase login/signIn
export const logInWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      Auth,
      email,
      password
    );
    return userCredential;
  } catch (error) {
    console.error("Error signing in: ", error);
    throw error;
  }
};

// Function for reset password
export const handlePasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(Auth, email);
    // .then((res) => {
    //   return res;
    // })
    // .catch((error) => {
    //   return error
    // })
  } catch (error) {
    throw error;
  }
};

// Firebase logout
export const firebaseLogOut = async () => {
  try {
    await signOut(Auth);
  } catch (error) {
    console.error("Error signing out: ", error);
    throw error;
  }
};


// ------------ Get refersh token --------------
// window.location.href("/");
// window.history.pushState(null, "", "/");
// Handle logout if user not exist
const handleLogout = () => {
  firebaseLogOut();
  localStorage.clear();
  // Redex state to show logout modal
  store.dispatch(showUnauthorizedUserMessage(true));
};

// Get Refresh Token
export const refreshAccessToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.error("No user found. Logging out.");
    handleLogout();
    return;
  }

  try {
    const idToken = await user.getIdToken(true);
    const idTokenResult = await user.getIdTokenResult(true);
    const refreshToken = user.refreshToken;

    return {
      idToken,
      accessToken: idTokenResult,
      refreshToken,
    };
  } catch (error) {
    console.log("Error: ", error)
    throw new Error("Failed to refresh tokens. Please log in again.");
  }
};

// Get logged in user immediately after firebase initialization
// (AUTH_CONFIG is the temprary setup for access token)
export const getLoggedInUser = (id) =>
  get(`/users/getUser/${id}`, {
    headers: {
      Authorization: `Bearer ${AUTH_CONFIG.access_token}`,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error?.response;
    });

// Get logged in user inside application after firebase initialization
export const getLoggedInUserAfterFirebaseInit = (id) =>
  get(`/users/getUser/${id}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error?.response;
    });

// Change/Update user login status
export const changeLoginStatus = (data) =>
  put("/users/updateUserLoginStatus", data, {
    headers: {
      Authorization: `Bearer ${AUTH_CONFIG.access_token}`,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error?.response;
    });


// Function to re-authenticate user and change password
export const updateOldPasswordViaFirebaseReAuth = async (email, oldPassword, newPassword, confirmPassword) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No user is currently logged in.");
  }

  // Check if new password matches confirm password
  if (newPassword !== confirmPassword) {
    throw new Error("New password and confirm password should be same.");
  }

  // Check if new password is the same as the old password
  if (oldPassword === newPassword) {
    throw new Error("You cannot set old password as a new password.");
  }

  // Create credentials using the email and old password
  const credential = EmailAuthProvider.credential(email, oldPassword);

  try {
    // Reauthenticate the user
    await reauthenticateWithCredential(user, credential);
    console.log("Reauthentication successful");

    // If reauthentication is successful, update the password
    await updatePassword(user, newPassword);
    console.log("Password updated successfully");
  } catch (error) {
    const Parsed_Error = JSON.parse(JSON.stringify(error));
    if (Parsed_Error?.code === "auth/invalid-credential") {
      console.log("Error: ", error);
      throw new Error("The old password is incorrect.");
    } else {
      console.log("Error: ", error)
      throw new Error("Error during reauthentication or updating password")
    }
  }
};
